import { logSentryError, trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Link, useMediaQuery, Theme } from '@mui/material';
import { PhoneIphone, CheckCircle } from '@mui/icons-material';
import theme from 'theme';
import { PatientObj, PatientApprovalStatus } from 'pages/Dashboard/types/patient.types';
import { mainNav } from 'utils/spaUrls';
import { approveRejectPatientStatus } from 'pages/Dashboard/services/patient.services';
import { useMutation, useQueryClient } from 'react-query';
import { useHttp } from 'hooks/use-fetch';
import Toast, { ToastProps, ToastType } from 'components/Common/Toast';
import HLDrawer, { ActionButtonProps } from 'components/HLDrawer';
import { patientUrls } from 'utils/apiUrls';

function PatientInfo({ patient }: { patient: PatientObj }) {
  return (
    <>
      <Typography variant='h5' fontWeight={400} className='fs-mask'>
        {patient.name?.firstName ? `${patient.name?.firstName} ${patient.name?.lastName}` : ''}
      </Typography>
      <Typography variant='body1' color={theme.custom.colors.lightTextPrimary} mb={1} className='fs-mask'>
        {patient.dateOfBirth ? `${patient.dateOfBirth.month}/${patient.dateOfBirth.day}/${patient.dateOfBirth.year}` : ''}
      </Typography>
    </>
  );
}
type ConfirmPatientDrawerProps = {
  open: boolean;
  onClose: () => void;
  patient: PatientObj;
};

function ConfirmPatientDrawer({ open, onClose, patient }: ConfirmPatientDrawerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('md'));

  const [confirmed, setConfirmed] = React.useState(false);
  const [declineConfirm, setDeclineConfirm] = React.useState(false);
  const [removed, setRemoved] = React.useState(false);
  const [toastProps, setToastProps] = React.useState<ToastProps | null>(null);

  const { providerId, http } = useHttp();

  const approveMutation = useMutation({
    mutationFn: () => approveRejectPatientStatus(
      http.post,
      providerId,
      patient.id as string,
      PatientApprovalStatus.APPROVED,
    ),
    onSuccess: () => {
      setConfirmed(true);
    },
    onError: (error) => {
      logSentryError(`Failed to approve the patient - ${error instanceof Error ? error.message : JSON.stringify(error)}`, {
        patientId: patient.id as string,
        providerId,
      });
      setToastProps({
        message: (error as Error).message,
        open: true,
        type: ToastType.error,
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => approveRejectPatientStatus(
      http.post,
      providerId,
      patient.id as string,
      PatientApprovalStatus.REJECTED,
    ),
    onSuccess: () => {
      setRemoved(true);
    },
    onError: (error) => {
      logSentryError(`Failed to reject the patient - ${error instanceof Error ? error.message : JSON.stringify(error)}`, {
        patientId: patient.id as string,
        providerId,
      });
      setToastProps({
        message: (error as Error).message,
        open: true,
        type: ToastType.error,
      });
    },
  });

  const handleClose = () => {
    trackMixpanelEvent(MixpanelEventName.CONFIRM_PATIENT_DRAWER_CLOSE_BUTTON_CLICKED, {
      'patient id': String(patient.id ?? ''),
    });
    setConfirmed(false);
    setDeclineConfirm(false);
    setRemoved(false);
    // Invalidate patient list query to refresh listing on close as well
    if (removed || confirmed) {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey?.[0];
          return (
            key === patientUrls.listPatients.queryUrl
        || key === patientUrls.patientsCount.queryUrl
          );
        },
      });
    }

    onClose();
  };

  // Button props for HLDrawer
  let primaryAction: ActionButtonProps | undefined;
  let secondaryAction: ActionButtonProps | undefined;

  if (!confirmed && !declineConfirm && !removed) {
    primaryAction = {
      label: 'This is my patient',
      ButtonProps: {
        size: 'large',
        fullWidth: true,
        variant: 'contained',
        sx: { background: theme.custom.colors.lightPrimaryMain, fontWeight: 500, fontSize: 15 },
        onClick: () => {
          trackMixpanelEvent(
            MixpanelEventName.CONFIRM_PATIENT_DRAWER_THIS_IS_MY_PATIENT_BUTTON_CLICKED,
            { 'patient id': String(patient.id ?? '') },
          );
          approveMutation.mutateAsync();
        },
        disabled: approveMutation.isLoading,
      },
    };
    secondaryAction = {
      label: 'Not my patient',
      ButtonProps: {
        size: 'large',
        fullWidth: true,
        variant: 'outlined',
        color: 'error',
        sx: { fontWeight: 500, fontSize: 15 },
        onClick: () => {
          trackMixpanelEvent(
            MixpanelEventName.CONFIRM_PATIENT_DRAWER_NOT_MY_PATIENT_BUTTON_CLICKED,
            { 'patient id': String(patient.id ?? '') },
          );
          setDeclineConfirm(true);
        },
      },
    };
  }
  else if (declineConfirm && !removed) {
    primaryAction = {
      label: 'Yes, remove this patient',
      ButtonProps: {
        size: 'large',
        fullWidth: true,
        variant: 'contained',
        color: 'error',
        sx: { fontWeight: 500, fontSize: 15 },
        onClick: () => {
          trackMixpanelEvent(
            MixpanelEventName.CONFIRM_PATIENT_DRAWER_REMOVE_PATIENT_BUTTON_CLICKED,
            { 'patient id': String(patient.id ?? '') },
          );
          rejectMutation.mutateAsync();
        },
      },
    };
    secondaryAction = {
      label: 'Cancel',
      ButtonProps: {
        size: 'large',
        fullWidth: true,
        variant: 'outlined',
        sx: { fontWeight: 500, fontSize: 15 },
        onClick: () => {
          trackMixpanelEvent(
            MixpanelEventName.CONFIRM_PATIENT_DRAWER_CANCEL_REMOVE_PATIENT_BUTTON_CLICKED,
            { 'patient id': String(patient.id ?? '') },
          );
          setDeclineConfirm(false);
        },
      },
    };
  }
  else if (confirmed) {
    primaryAction = {
      label: 'View patient profile',
      ButtonProps: {
        size: 'large',
        fullWidth: isMobile,
        variant: 'contained',
        sx: { background: theme.custom.colors.lightPrimaryMain, fontWeight: 500, fontSize: 15 },
        onClick: () => {
          trackMixpanelEvent(
            MixpanelEventName.CONFIRM_PATIENT_DRAWER_VIEW_PATIENT_PROFILE_CLICKED,
            {
              'patient id': String(patient.id ?? ''),
            },
          );
          navigate(mainNav.patientDetail(patient.id));
          handleClose();
        },
      },
    };
  }
  else if (removed) {
    primaryAction = {
      label: 'Close',
      ButtonProps: {
        size: 'large',
        fullWidth: isMobile,
        variant: 'contained',
        sx: { background: theme.custom.colors.lightPrimaryMain, fontWeight: 500, fontSize: 15 },
        onClick: () => {
          trackMixpanelEvent(
            MixpanelEventName.CONFIRM_PATIENT_DRAWER_CLOSE_BUTTON_CLICKED,
            {
              'patient id': String(patient.id ?? ''),
            },
          );
          handleClose();
        },
      },
    };
  }

  // Helper to render desktop actions using primaryAction/secondaryAction
  const renderDesktopActions = () => {
    if (isMobile) return null;

    // If only primaryAction is defined (removed state)
    if (primaryAction && !secondaryAction) {
      return (
        <Button {...primaryAction.ButtonProps}>{primaryAction.label}</Button>
      );
    }
    // If both actions are defined (declineConfirm or !confirmed)
    if (primaryAction && secondaryAction) {
      return (
        <Box sx={{ display: 'flex', gap: 2.5, mt: declineConfirm ? 2 : 'auto' }}>
          <Button {...primaryAction.ButtonProps}>{primaryAction.label}</Button>
          <Button {...secondaryAction.ButtonProps}>{secondaryAction.label}</Button>
        </Box>
      );
    }
    return null;
  };

  // Content for HLDrawer
  const requestSentText = React.useMemo(() => {
    if (!patient.dateAdded) return '';
    const now = new Date();
    const addedDate = new Date(patient.dateAdded);

    const diffMs = now.getTime() - addedDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffDays === 0) return 'Request sent today';
    if (diffDays === 1) return 'Request sent 1 day ago';
    if (diffDays < 7) return `Request sent ${diffDays} days ago`;
    if (diffWeeks < 4) return `Request sent ${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
    // If weeks >= 4 but months is 0, still say 4 weeks ago
    if (diffWeeks >= 4 && diffMonths === 0) return 'Request sent 4 weeks ago';
    if (diffMonths < 12) return `Request sent ${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    return `Request sent ${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  }, [patient.dateAdded]);

  let content;
  if (removed) {
    content = (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='flex-start'
        bgcolor='#fff'
        borderRadius={3}
        p={2.5}
      >
        <CheckCircle sx={{ color: theme.custom.colors.lightSuccessMain, fontSize: 80, mb: 6 }} />
        <Typography variant='h6' fontWeight={500} mb={2.5} align='center' color={theme.custom.colors.lightTextPrimary}>Patient removed</Typography>
        <Typography variant='body1' mb={4} align='center' color={theme.custom.colors.lightTextPrimary}>This patient will no longer appear in your patient list.</Typography>
        {renderDesktopActions()}
      </Box>
    );
  }
  else if (declineConfirm) {
    content = (
      <>
        <PatientInfo patient={patient} />
        <Box bgcolor='#fff' p={2.5} mt={3} borderRadius={3}>
          <Typography mb={1} variant='h6' fontWeight={500} color={theme.custom.colors.lightTextPrimary}>
            Are you sure this is not your patient?
          </Typography>
          <Typography mb={4} color={theme.custom.colors.lightTextPrimary} variant='body1'>
            This patient will no longer appear in your patient list.
          </Typography>
          {renderDesktopActions()}
        </Box>
      </>
    );
  }
  else if (!confirmed) {
    content = (
      <>
        <PatientInfo patient={patient} />
        <Typography mb={2} color={theme.custom.colors.lightTextSecondary} variant='subtitle2' fontSize={14}>{requestSentText}</Typography>
        <Box sx={{ background: '#e3f2fd', borderRadius: 1, p: 2, mb: 4, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', pt: 0.5 }}>
            <PhoneIphone fontSize='large' />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography mb={2} variant='body1' color={theme.custom.colors.lightTextPrimary}>
              This patient has downloaded the Headlamp mobile app and
              <Typography component='span' fontWeight={700} ml={0.5}>
                identified you as their clinician.
              </Typography>
            </Typography>
            <Typography variant='body1' color={theme.custom.colors.lightTextPrimary}>
              Click &quot;This is my patient&quot; to access their health records.
              <br />
              <br />
              Click &quot;Not my patient&quot; if this is not your patient.
            </Typography>
          </Box>
        </Box>
        {renderDesktopActions()}
        <Box sx={{ mt: 3 }}>
          <Typography variant='caption' color={theme.custom.colors.lightTextSecondary} mb={1.5}>
            This indicates that you have provided notice to your patient(s) and
            obtained their authorization to
            pull patient medical record history.
            By clicking &quot;This is my patient&quot;, I represent and warrant that I
            have provided any legally required
            privacy notice (e.g., HIPAA Privacy Notice)
            and obtained patient authorization as required under
            applicable law from the patient
            listed that their medical information will be provided to Headlamp.
            I further understand and agree that such information will be
            processed by Headlamp under the
            Headlamp
            {' '}
            <Link href='https://www.headlamp.com/terms' target='_blank' rel='noopener noreferrer' underline='hover'>Terms of Service</Link>
            {' '}
            and
            {' '}
            <Link href='https://www.headlamp.com/privacy' target='_blank' rel='noopener noreferrer' underline='hover'>Privacy Policy</Link>
            .
          </Typography>
        </Box>
      </>
    );
  }
  else {
    content = (
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='flex-start'
        bgcolor='#fff'
        borderRadius={3}
        p={2.5}
      >
        <CheckCircle sx={{ color: theme.custom.colors.lightSuccessMain, fontSize: 80, mb: 6 }} />
        <Typography
          variant='h6'
          color={theme.custom.colors.lightTextPrimary}
          fontWeight={500}
          mb={2}
          align='center'
        >
          Patient confirmed
        </Typography>
        <Typography
          variant='body1'
          mb={6}
          align='center'
          color={theme.custom.colors.lightTextPrimary}
        >
          You can now view their profile.
        </Typography>
        {renderDesktopActions()}
      </Box>
    );
  }

  return (
    <>
      {toastProps && (
        <Toast
          {...toastProps}
          onClose={() => setToastProps(null)}
        />
      )}
      <HLDrawer
        open={open}
        onClose={handleClose}
        title='Confirm patient request'
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      >
        {content}
      </HLDrawer>
    </>
  );
}

export default ConfirmPatientDrawer;
